import React, { FC, useMemo } from 'react';
import cx from 'classnames';
import { Territory } from 't2-graphql-types';
import { BREADCRUMBS_ID } from './constants';
import { useNavbarHeight } from '../Navbar/hooks/useNavbarHeight';
import useDetectScrollDirection, { ScrollDirection } from '../DiscordButton/hooks/useDetectScrollDirection';
import { useT2Query } from '../../hooks/useT2Query';
import { getBreadcrambsArticleQuery } from './queries/getBreadcrambsArticleQuery';
import getTerritoryPageUrl from '../../services/pagesUrl/getTerritoryPageUrl';
import { getArticlePageUrl } from '../../services/pagesUrl/getArticlePageUrl';
import { getPromptPageUrl } from '../../services/pagesUrl/getPromptPageUrl';
import { useAnalytics } from '../AnalyticsProvider/AnalyticsProvider';
import { Breadcrumbs } from './Breadcrumbs';
import { BreadcrumbLink } from './BreadcrumbLink';
import { BreadcrumbImageAdornment } from './BreadcrumbImageAdornment';
import { DEFAULT_TERRITORY_LOGO } from '../TerritoryHeader/constants';
import { DEFAULT_ARTICLE_IMAGE } from '../ArticleCard/constants';
import { TBreadcrumbsListItemProps } from './type';

type TerritoryData = {
  id: Territory['id'];
  name: Territory['name'];
  slug: Territory['slug'];
  logo?: Territory['logo'];
};

type TBreadcrumbsListContainerProps =
  | {
      articleId: string;
      type: 'post' | 'prompt';
      territory?: TerritoryData;
      label?: string;
      labelHref?: string;
      parentLabel?: string;
      parentHref?: string;
      className?: string;
    }
  | {
      articleId?: string;
      type: 'territory';
      territory: TerritoryData;
      label?: string;
      labelHref?: string;
      parentLabel?: string;
      parentHref?: string;
      className?: string;
    };

export const BreadcrumbsListContainer: FC<TBreadcrumbsListContainerProps> = ({
  articleId,
  territory,
  type,
  label,
  labelHref,
  parentLabel,
  parentHref,
  className,
}) => {
  const navbarHeight = useNavbarHeight();
  const scrollDirection = useDetectScrollDirection();
  const { sendEvent } = useAnalytics();

  const translateYValue = useMemo(
    () => (scrollDirection === ScrollDirection.up ? 0 : -navbarHeight),
    [scrollDirection, navbarHeight],
  );

  const style = {
    ...(type !== 'territory'
      ? { marginTop: navbarHeight, transform: `translateY(${translateYValue}px)` }
      : { top: navbarHeight, position: 'absolute' as const }),
  };

  const { data } = useT2Query(getBreadcrambsArticleQuery, {
    skip: !articleId,
    returnPartialData: true,
    variables: {
      articleId,
    },
  });

  const territoryData = data?.article?.territory || territory;
  const article = data?.article;

  if (!territoryData) {
    return null;
  }

  const isPost = (type: string | undefined) => type === 'post';
  const isPrompt = (type: string | undefined) => type === 'prompt';
  const isText = (type: string | undefined) => type === 'text';
  const isTerritory = (type: string | undefined) => type === 'territory';

  const defaultImageByType = (type: string | undefined) => {
    if (isPost(type) || isPrompt(type)) {
      return DEFAULT_ARTICLE_IMAGE;
    }

    if (isTerritory(type)) {
      return DEFAULT_TERRITORY_LOGO;
    }

    return '';
  };

  const breadcrumbListItems: TBreadcrumbsListItemProps[] = [
    {
      label: `t/${territoryData?.name}`,
      href: getTerritoryPageUrl(territoryData?.slug),
      imgSrc: territoryData?.logo?.publicUrlTransformed,
      type: 'territory',
      onClick: () => sendEvent('territory_breadcrumb_click', { territory_id: territoryData?.id }),
    },
  ];
  if (parentLabel) {
    breadcrumbListItems.push({
      label: parentLabel,
      href: parentHref,
      type: 'text',
    });
  }
  if (type === 'territory') {
    breadcrumbListItems.push({
      label: label || '',
      type: 'text',
      href: labelHref,
    });
  } else {
    const href = type === 'post' ? getArticlePageUrl(article?.slug) : getPromptPageUrl(article?.slug);
    breadcrumbListItems.push({
      label: article?.title!,
      href,
      imgSrc: article?.coverImage?.publicUrlTransformed,
      type,
      onClick: () =>
        type === 'post'
          ? sendEvent('post_breadcrumb_click', { post_id: article?.id })
          : sendEvent('prompt_breadcrumb_click', { prompt_id: article?.id }),
    });
  }

  const renderBreadcrumbItem = (item: TBreadcrumbsListItemProps, index: number) => (
    <BreadcrumbLink
      key={index}
      href={item.href!}
      leftAdornment={
        isText(item.type) ? null : (
          <BreadcrumbImageAdornment
            rounded={isPost(item.type) || isPrompt(item.type) ? 'sm' : 'full'}
            src={item.imgSrc! || defaultImageByType(item.type)}
            alt={`${item.label} logo`}
          />
        )
      }
      onClick={item.onClick}
    >
      {item.label}
    </BreadcrumbLink>
  );

  return (
    <div
      id={BREADCRUMBS_ID}
      className={cx(
        'w-full max-w-[1440px] py-2 fixed flex z-40 duration-200 transition bg-t2-neutral-grey01',
        className,
      )}
      style={style}
    >
      <Breadcrumbs>{breadcrumbListItems.map(renderBreadcrumbItem)}</Breadcrumbs>
    </div>
  );
};
